import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { Card, CardHeader, DataTable, ErrorPanel, StatusPill } from "../../ui/components";
import { SkeletonTable } from "../../ui/skeletons";
import type {
  BillingStatus,
  CustomDomain,
  EmailSettings,
  OIDCConnectionSummary,
  WebhookEndpoint,
  WorkspaceSubdomain,
} from "./types";

interface OverviewRow {
  id: string;
  name: string;
  status: string;
  detail: string;
}

// One page that answers "is anything broken?" without opening six panels. Everything here is read
// from the same endpoints the panels use, so a status shown on this row cannot disagree with the
// panel it sends you to.
export function IntegrationsOverview({ onOpen }: { onOpen: (section: string) => void }) {
  const auth = useAuth();
  const canBill = membershipAllows(auth.session?.selectedMembership, "tenant.manage");
  const billing = useQuery({
    queryKey: ["billing", "status"],
    queryFn: () => api<BillingStatus>("/api/v1/billing/status"),
    enabled: canBill,
  });
  const subdomain = useQuery({
    queryKey: ["integrations", "subdomain"],
    queryFn: () => api<WorkspaceSubdomain>("/api/v1/integrations/subdomain"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api<ListResponse<CustomDomain>>("/api/v1/domains"),
  });
  const email = useQuery({
    queryKey: ["messaging", "email-settings"],
    queryFn: () => api<EmailSettings>("/api/v1/messaging/email-settings"),
  });
  const webhooks = useQuery({
    queryKey: ["messaging", "webhooks"],
    queryFn: () => api<ListResponse<WebhookEndpoint>>("/api/v1/messaging/webhook-endpoints"),
  });
  const connections = useQuery({
    queryKey: ["sso", "connections"],
    queryFn: () => api<ListResponse<OIDCConnectionSummary>>("/api/v1/auth/sso/connections"),
    retry: false,
  });

  if (subdomain.isLoading || domains.isLoading || email.isLoading) {
    return <SkeletonTable columns={4} rows={6} />;
  }

  const liveDomains = (domains.data?.items ?? []).filter((item) => !item.releasedAt);
  const verifiedDomains = liveDomains.filter((item) => item.status === "verified");
  const activeWebhooks = (webhooks.data?.items ?? []).filter((item) => item.active);
  const enabledConnections = (connections.data?.items ?? []).filter((item) => item.enabled);
  const rows: OverviewRow[] = [
    {
      id: "payments",
      name: "Customer payments",
      status: canBill ? (billing.data?.connectedAccount.state ?? "unknown") : "owner_only",
      detail: canBill
        ? "Stripe Connect settles the payments your customers make to you."
        : "Only the workspace owner can see or change the payment account.",
    },
    {
      id: "addresses",
      name: "Workspace address",
      status: subdomain.data?.status ?? "unknown",
      detail: subdomain.data?.hostname || "No address has been published yet.",
    },
    {
      id: "addresses",
      name: "Custom domains",
      status: liveDomains.length ? (verifiedDomains.length ? "verified" : "pending") : "none",
      detail: liveDomains.length
        ? `${verifiedDomains.length} of ${liveDomains.length} verified`
        : "No hostname of your own is connected.",
    },
    {
      id: "email",
      name: "Email delivery",
      status: email.data?.configured ? "configured" : "not_configured",
      // The sending address rather than the transport. "Sent through Capture" named an internal
      // driver that no workspace chooses and none can change, so it told a reader nothing they
      // could act on. The address is the part that actually differs between workspaces.
      detail: email.data?.fromAddress
        ? `Sending from ${email.data.fromAddress}`
        : "No sending address chosen yet.",
    },
    {
      id: "webhooks",
      name: "Webhooks",
      status: activeWebhooks.length ? "active" : "none",
      detail: activeWebhooks.length
        ? `${activeWebhooks.length} endpoint${activeWebhooks.length === 1 ? "" : "s"} receiving events`
        : "No other system is being notified.",
    },
    {
      id: "sso",
      name: "Workforce SSO",
      status: enabledConnections.length ? "enabled" : "none",
      detail: enabledConnections.length
        ? enabledConnections.map((item) => item.name).join(", ")
        : "Staff sign in with a password and two-step verification.",
    },
  ];

  return (
    <div className="stack">
      {subdomain.error || domains.error || email.error ? (
        <ErrorPanel error={subdomain.error ?? domains.error ?? email.error} />
      ) : null}
      <Card>
        <CardHeader
          title="Connected services"
          description="Everything this workspace connects to on the outside, and whether it is working."
        />
        <DataTable
          caption="Connected services"
          items={rows}
          getKey={(item) => `${item.id}:${item.name}`}
          columns={[
            { label: "Integration", render: (item) => <strong>{item.name}</strong> },
            { label: "Status", render: (item) => <StatusPill value={item.status} /> },
            { label: "Detail", render: (item) => item.detail },
            {
              label: "Action",
              render: (item) => (
                <button
                  className="button secondary small"
                  type="button"
                  onClick={() => onOpen(item.id)}
                >
                  Open {item.name.toLowerCase()}
                </button>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
